"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import { AnimatePresence, motion } from 'motion/react';
import { Tabs } from '../Tabs/Tabs';
import { NotificationItem, type NotificationItemProps } from '../NotificationItem/NotificationItem';
import { Icon } from '../../icons';
import { Banner } from '../Banner/Banner';
import { Button } from '../Button/Button';
import { EmptyState } from '../EmptyState/EmptyState';
import './NotificationPanel.css';

export interface NotificationPanelItem extends NotificationItemProps {
  id: string;
}

export interface NotificationPanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback to close the panel */
  onClose: () => void;
  /** Notification items */
  notifications: NotificationPanelItem[];
  /** Total unread count across all server pages */
  unreadCount?: number;
  /** Callback when "Mark all read" is clicked */
  onMarkAllRead?: () => void;
  /** Callback when a notification is clicked */
  onNotificationClick?: (id: string) => void;
  /** Whether notifications are loading for the first time */
  loading?: boolean;
  /** Reader-facing load error */
  error?: string | null;
  /** Callback to retry a failed load */
  onRetry?: () => void;
  /** Whether mark-all-read is currently saving */
  markAllReadBusy?: boolean;
  /** Settings destination for notification preferences */
  settingsHref?: string;
  /** Additional CSS class */
  className?: string;
}

const tabItems = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
];

export const NotificationPanel = React.memo(function NotificationPanel({
  open,
  onClose,
  notifications,
  unreadCount: totalUnreadCount,
  onMarkAllRead,
  onNotificationClick,
  loading = false,
  error = null,
  onRetry,
  markAllReadBusy = false,
  settingsHref,
  className = '',
}: NotificationPanelProps) {
  const [activeTab, setActiveTab] = useState<string>('all');
  const panelRef = useRef<HTMLElement | null>(null);

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'all') return notifications;
    if (activeTab === 'unread') return notifications.filter((n) => n.unread);
    return notifications;
  }, [notifications, activeTab]);
  const loadedUnreadCount = useMemo(
    () => notifications.filter((notification) => notification.unread).length,
    [notifications],
  );
  const unreadCount = totalUnreadCount ?? loadedUnreadCount;

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
  }, [open]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="notification-panel__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={handleOverlayClick}
          onKeyDown={handleKeyDown}
        >
          <motion.aside
            ref={panelRef}
            className={`notification-panel ${className}`.trim()}
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '30%', opacity: 0, filter: 'blur(4px)' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            role="dialog"
            aria-label="Notifications"
            aria-modal="true"
            tabIndex={-1}
          >
            <div className="notification-panel__header">
              <h2 className="notification-panel__title">Notifications</h2>
              <div className="notification-panel__header-actions">
                {onMarkAllRead && unreadCount > 0 && (
                  <button
                    className="notification-panel__mark-read"
                    onClick={onMarkAllRead}
                    disabled={markAllReadBusy}
                    aria-busy={markAllReadBusy || undefined}
                  >
                    {markAllReadBusy ? 'Marking read…' : 'Mark all read'}
                  </button>
                )}
                {settingsHref ? (
                  <Link
                    className="notification-panel__settings"
                    href={settingsHref}
                    aria-label="Notification settings"
                    onClick={onClose}
                  >
                    <Icon name="Settings" size={20} />
                  </Link>
                ) : null}
                <button
                  className="notification-panel__close"
                  onClick={onClose}
                  aria-label="Close notifications"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <div className="notification-panel__tabs">
              <Tabs
                items={tabItems}
                value={activeTab}
                onValueChange={(v) => setActiveTab(v || 'all')}
                size="sm"
              />
            </div>

            <div className="notification-panel__list">
              {error ? (
                <div className="notification-panel__state">
                  <Banner variant="failure" title="Could not load notifications">
                    {error}
                  </Banner>
                  {onRetry ? (
                    <Button variant="secondary" size="sm" onClick={onRetry}>
                      Try again
                    </Button>
                  ) : null}
                </div>
              ) : loading ? (
                <div
                  className="notification-panel__state"
                  role="status"
                  aria-live="polite"
                >
                  <span className="notification-panel__spinner" aria-hidden="true" />
                  <p>Loading notifications…</p>
                </div>
              ) : filteredNotifications.length === 0 ? (
                <EmptyState
                  className="notification-panel__empty"
                  icon="Bell"
                  title={
                    activeTab === 'unread'
                      ? 'No unread notifications'
                      : "You're all caught up"
                  }
                  description={
                    activeTab === 'unread'
                      ? 'Unread survey response notifications will appear here.'
                      : 'New completed survey responses will appear here.'
                  }
                />
              ) : (
                filteredNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    {...notification}
                    onClick={() => onNotificationClick?.(notification.id)}
                  />
                ))
              )}
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
